// What a feedback report carries to help reproduce it (2026-10-27): the build, the browser and screen, and the last API
// errors. Held only in this tab's memory until a report is sent, and only what's listed here: never a request's or an
// answer's body, a header, a token, a query string or an exception's text.
export interface ApiErrorNote { at: string; method: string; route: string; status: number; code?: string; request_id?: string }

const KEEP_MS = 5 * 60 * 1000, MAX = 20;
const errors: ApiErrorNote[] = [];
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** A path as a route template: ids and numbers become {id}, the query and hash go. */
export function template(path: string): string {
  return path.split(/[?#]/)[0].replace(UUID, "{id}").split("/").map((p) => (/^\d+$/.test(p) ? "{id}" : p)).join("/").slice(0, 200);
}

export function recordApiError(method: string, path: string, status: number, code?: string, requestId?: string | null): void {
  errors.push({ at: new Date().toISOString(), method, route: template(path), status, ...(code ? { code } : {}),
                ...(requestId ? { request_id: requestId.slice(0, 80) } : {}) });
  while (errors.length > MAX) errors.shift();
}

export function recentErrors(now = Date.now()): ApiErrorNote[] {
  return errors.filter((e) => now - Date.parse(e.at) < KEEP_MS).map((e) => ({ ...e }));
}

export function clearErrors(): void { errors.length = 0; }

function browserOf(ua: string): { browser: string; os: string } {
  const pick = (re: RegExp, name: string) => { const m = ua.match(re); return m ? `${name} ${m[1]}` : null; };
  const browser = pick(/Edg\/(\d+)/, "Edge") ?? pick(/Firefox\/(\d+)/, "Firefox") ?? pick(/Chrome\/(\d+)/, "Chrome")
    ?? pick(/Version\/(\d+).*Safari/, "Safari") ?? "another browser";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS X|Macintosh/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android"
    : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "another system";
  return { browser, os };
}

/** The page details a report may carry, as the person reviews them before sending. */
export function pageDetails(screen: string): Record<string, unknown> {
  const { browser, os } = browserOf(navigator.userAgent);
  const theme = document.documentElement.dataset.theme ?? (matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light");
  return { version: typeof __MAJLIS_BUILD__ === "string" ? __MAJLIS_BUILD__ : "dev", browser, os, width: innerWidth, height: innerHeight,
           dpr: Math.round((devicePixelRatio || 1) * 100) / 100, theme, language: navigator.language?.slice(0, 16),
           reduced_motion: !!matchMedia?.("(prefers-reduced-motion: reduce)")?.matches, screen: screen.slice(0, 120),
           errors: recentErrors() };
}
